import { useMemo, useRef, useState } from 'react';
import {
  barCount,
  barOfBeat,
  barsOfRange,
  beatIndexAt,
  beatInBar,
  gridEnd,
} from '@/core/analysis/grid';
import { formatTime } from '@/lib/format';
import { useAnimationFrame } from '@/lib/useAnimationFrame';
import { SHORTCUTS } from '@/lib/shortcuts';
import { useHotkeys } from '@/lib/useHotkeys';
import { useTimeIndex } from '@/lib/useTimeIndex';
import { getTransport, LOOP_PRESETS, usePlayerStore } from '@/state/playerStore';
import { useDisplayResult } from '@/state/useDisplayResult';
import { LoopIcon, MuteIcon, PauseIcon, PlayIcon, VolumeIcon } from '@/ui/icons';
import { Segmented } from '@/ui/Segmented';
import { PracticePanel } from './PracticePanel';
import { Waveform } from './Waveform';
import styles from './TransportBar.module.css';

/** Sticky playback controls (F3.1, F3.2, F3.4, F3.9; design: Player transport). */
export function TransportBar() {
  const { playing, rate, loop, loopBars, volume, muted, visualOffsetMs } = usePlayerStore();
  const result = useDisplayResult();
  const { togglePlay, seek, setRate, setLoopBars, toggleLoop, setVolume, toggleMute } =
    usePlayerStore();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const metronome = usePlayerStore((s) => s.metronome);
  // YouTube songs: no practice sounds (they need the song's own audio clock).
  const isVideo = usePlayerStore((s) => Boolean(s.song?.youtubeId));
  const setPractice = usePlayerStore((s) => s.setPractice);
  const timeRef = useRef<HTMLSpanElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  const playheadRef = useRef<HTMLSpanElement>(null);
  const seekRef = useRef<HTMLInputElement>(null);
  const seeking = useRef(false);

  useHotkeys({
    '?': () => {
      setPracticeOpen(false);
      setShortcutsOpen((open) => !open);
    },
    m: isVideo
      ? undefined
      : () => {
          setPractice({ metronome: !metronome });
        },
    Escape:
      shortcutsOpen || practiceOpen
        ? () => {
            setShortcutsOpen(false);
            setPracticeOpen(false);
          }
        : undefined,
  });

  const transport = getTransport();
  const duration = transport?.duration ?? result?.duration ?? 0;
  // The video plays only at the speeds YouTube offers.
  const rateSteps = transport?.rateSteps;
  const speedMin = Math.round((rateSteps?.[0] ?? 0.5) * 100);
  const speedMax = Math.round((rateSteps?.at(-1) ?? 1.5) * 100);
  const speedStep = rateSteps ? 25 : 5;
  const grid = result?.beatGrid;
  const bars = grid ? barCount(grid) : 0;
  const segments = useMemo(() => result?.segments ?? [], [result]);
  const starts = useMemo(() => segments.map((s) => s.start), [segments]);
  const activeSegment = useTimeIndex(starts, visualOffsetMs);

  useAnimationFrame(() => {
    const t = getTransport();
    if (!t || !grid) return;
    const pos = t.getPosition(visualOffsetMs);
    if (timeRef.current) timeRef.current.textContent = formatTime(pos);
    if (barRef.current) {
      const beat = beatIndexAt(grid, pos);
      barRef.current.textContent =
        beat < 0
          ? 'Before the first beat'
          : `Bar ${barOfBeat(grid, beat)} of ${bars} · beat ${beatInBar(grid, beat)}`;
    }
    if (playheadRef.current && duration > 0)
      playheadRef.current.style.left = `${(100 * pos) / duration}%`;
    if (seekRef.current && !seeking.current) seekRef.current.value = String(pos);
  });

  const loopLabel = (() => {
    if (!loop || !grid) return 'Loop A–B';
    const [a, b] = barsOfRange(grid, loop);
    return a === b ? `Loop bar ${a}` : `Loop bars ${a}–${b}`;
  })();

  const firstStart = segments[0]?.start ?? 0;
  const lastEnd = grid ? gridEnd(grid, duration) : duration;
  const pct = (seconds: number) => `${duration > 0 ? (100 * seconds) / duration : 0}%`;

  return (
    <div className={styles.bar} role="region" aria-label="Playback controls">
      {practiceOpen && !isVideo && <PracticePanel />}
      {shortcutsOpen && (
        <div role="dialog" aria-label="Keyboard shortcuts" className={styles.shortcuts}>
          <span className={styles.shortcutsTitle}>Keyboard shortcuts</span>
          {SHORTCUTS.map(([what, key]) => (
            <div key={what} className={styles.shortcut}>
              <span>{what}</span>
              <kbd>{key}</kbd>
            </div>
          ))}
        </div>
      )}

      <div className={styles.inner}>
        <div className={styles.overview} data-waveform={result?.peaks ? true : undefined}>
          <div className={styles.regions} aria-hidden="true">
            {firstStart > 0 && (
              <span className={styles.region} data-kind="gap" style={{ flexGrow: firstStart }} />
            )}
            {segments.map((s, i) => (
              <span
                key={s.startBeat}
                className={styles.region}
                data-kind={s.chord ? (i % 2 ? 'odd' : 'even') : 'gap'}
                data-active={i === activeSegment || undefined}
                style={{ flexGrow: s.end - s.start }}
              />
            ))}
            {lastEnd < duration && (
              <span
                className={styles.region}
                data-kind="gap"
                style={{ flexGrow: duration - lastEnd }}
              />
            )}
          </div>
          {result?.peaks && <Waveform peaks={result.peaks} />}
          {loop && (
            <span
              className={styles.loopOverlay}
              style={{ left: pct(loop.start), width: pct(loop.end - loop.start) }}
            />
          )}
          <span ref={playheadRef} className={styles.playhead} />
          <input
            ref={seekRef}
            className={styles.seek}
            type="range"
            min={0}
            max={duration}
            step={0.01}
            defaultValue={0}
            aria-label="Song position"
            disabled={!transport}
            onPointerDown={() => {
              seeking.current = true;
            }}
            onPointerUp={() => {
              seeking.current = false;
            }}
            onChange={(e) => {
              seek(Number(e.target.value));
            }}
          />
        </div>

        <div className={styles.controls}>
          <button
            type="button"
            className={styles.play}
            aria-label={playing ? 'Pause' : 'Play'}
            disabled={!transport}
            onClick={() => void togglePlay()}
          >
            {playing ? <PauseIcon size={22} /> : <PlayIcon size={22} />}
          </button>

          <div className={styles.time}>
            <span className={styles.clock}>
              <span ref={timeRef} data-testid="position">
                0:00
              </span>{' '}
              / {formatTime(duration)}
            </span>
            <span ref={barRef} className={styles.barBeat} />
          </div>

          <label className={styles.speed}>
            Speed
            <input
              type="range"
              min={speedMin}
              max={speedMax}
              step={speedStep}
              value={Math.round(rate * 100)}
              aria-valuetext={`${Math.round(rate * 100)}%`}
              onChange={(e) => {
                setRate(Number(e.target.value) / 100);
              }}
            />
            <span className={styles.speedValue}>{Math.round(rate * 100)}%</span>
          </label>

          <div className={styles.loopGroup}>
            <button
              type="button"
              className={styles.loop}
              aria-pressed={loop !== null}
              onClick={toggleLoop}
            >
              <LoopIcon size={16} />
              {loopLabel}
            </button>
            <Segmented
              label="Loop length in bars"
              options={LOOP_PRESETS.map((n) => ({
                value: n,
                label: String(n),
                ariaLabel: `Loop ${n} ${n === 1 ? 'bar' : 'bars'}`,
              }))}
              value={loop ? loopBars : null}
              onChange={(n) => {
                setLoopBars(loop !== null && loopBars === n ? null : n);
              }}
            />
          </div>

          <div className={styles.end}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={muted ? 'Unmute' : 'Mute'}
              aria-pressed={muted}
              onClick={toggleMute}
            >
              {muted ? <MuteIcon size={18} /> : <VolumeIcon size={18} />}
            </button>
            <input
              className={styles.volume}
              type="range"
              min={0}
              max={100}
              value={muted ? 0 : Math.round(volume * 100)}
              aria-label="Volume"
              onChange={(e) => {
                setVolume(Number(e.target.value) / 100);
              }}
            />
            {!isVideo && (
              <button
                type="button"
                className={styles.textButton}
                aria-pressed={practiceOpen}
                aria-expanded={practiceOpen}
                onClick={() => {
                  setShortcutsOpen(false);
                  setPracticeOpen(!practiceOpen);
                }}
              >
                Practice
              </button>
            )}
            <button
              type="button"
              className={styles.iconButton}
              aria-label="Keyboard shortcuts"
              aria-pressed={shortcutsOpen}
              onClick={() => {
                setPracticeOpen(false);
                setShortcutsOpen(!shortcutsOpen);
              }}
            >
              ?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
